/** @jsx jsx */
import { css, jsx } from '@emotion/react';
import React from 'react';
import { H6 } from '@blueprintjs/core';
import { GenericObject } from 'interfaces/GenericObject';
import { Sizes } from 'styles/variables-slykit';
import { InferredType, parseInferredType, flattenInferredTypeValueToString } from 'utilities/parseInferredType';
import { HighlightTerms } from './textHighlighter';

export interface DocumentContentProps {
	/** the SRS data item, could be anything? */
	item: GenericObject<any>, // TODO: type this
	/** which body properties to show */
	bodyProperties?: string[],
	/** display property name: values in a single column instead of the default double */
	singleColumn?: boolean,
	/** if an item.property has an empty value, hide it */
	hideEmptyValues?: boolean,
}

export interface DocumentContentSearchProps extends DocumentContentProps {
	/** the terms that have been searched for */
	terms?: string[],
	/** the names of the fields that the terms are searched for in */
	fields?: string[],
}

/** default component to display and format the DocumentViewer's Body/Content */
export const DefaultDocumentContent: React.FunctionComponent<DocumentContentSearchProps> = ({
	item,
	bodyProperties,
	terms,
	fields,
	singleColumn = false,
	hideEmptyValues = false,
	...props
}) => {
	const properties = bodyProperties ?? Object.keys(item.propertyValues ?? []);

	// console.log(item.propertyValues);

	const renderProperties = properties
		.map((property) => {
			// TODO: properties could include shortcut links to apply facet filters

			let propertyValue = item.propertyValues[property];
			let typeInferredPropertyValue = parseInferredType(propertyValue);

			if (hideEmptyValues && typeInferredPropertyValue.type === InferredType.Undefined) {
				return null; // .filter() out below
			}

			// console.log(property, typeInferredPropertyValue, propertyValue);

			// prettifying how any array value is viewed (all on new lines)
			if (typeInferredPropertyValue.type === InferredType.Array) {
				typeInferredPropertyValue = {
					type: InferredType.String,
					value: flattenInferredTypeValueToString(typeInferredPropertyValue),
				};
			}

			const isSearched = fields.includes(property);
			const isLongString = propertyValue?.length > 140;

			return {
				property,
				propertyValue,
				typeInferredPropertyValue,
				isSearched,
				isLongString,
			};
		})
		.filter((property) => property != null);

	return (
		<div css={[contentWrapperStyle, singleColumn ? undefined : contentWrapperGridStyle]}>
			{renderProperties.map(({ property, propertyValue, typeInferredPropertyValue, isSearched, isLongString }) => {
				const propertyValueCss = [propertyValueStyle, isLongString ? twoColStyle : undefined];
				let propertySlug = property
					.replace(/^\s+|\s+$/g, '') // Trim
					.toLowerCase() // Lowercase
					.replace(/\s+/g, '-'); // Spaces to dashes

				return (
					<React.Fragment key={property}>
						<H6
							children={property}
							css={[propertyLabelStyle, isLongString ? twoColStyle : undefined]}
							cy-test={`${propertySlug}-label`}
						/>

						{typeInferredPropertyValue.type === InferredType.Date ? (
							// TODO: render the rest of the InferredTypes appropriately
							<div
								css={propertyValueCss}
								children={typeInferredPropertyValue.value.format('MM/DD/YYYY')}
								cy-test={`${propertySlug}-text`}
							/>
						) : (
							<HighlightTerms
								terms={isSearched ? terms : undefined}
								wrapperElement={'p'}
								children={typeInferredPropertyValue.value?.toString() ?? ''}
								noTextMessage={'none'}
								css={propertyValueCss}
								cy-test={`${propertySlug}-text`}
							/>
						)}
					</React.Fragment>
				);
			})}
		</div>
	);
};
const propertyLabelStyle = css`
	/* margin: 0; */
	margin: 1em 0 0.25em 0;
`;
const propertyValueStyle = css`
	margin: 0;
	white-space: pre-wrap;
`;
const twoColStyle = css`
	grid-column: span 2;
`;
const contentWrapperStyle = css`
	padding: ${Sizes.sm} ${Sizes.lg} ${Sizes.xxxlg}; 
`;
const contentWrapperGridStyle = css`
	display: grid;
	grid-template-columns:  // minmax(auto, 40%)
		auto
		minmax(1rem, 1fr);
	align-items: baseline;
	grid-gap: 0 ${Sizes.md};
`;
